import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { CustomDialog } from './StyledComponents';
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from './StyledButton';

const FIVE_MIN = 300; // 5 phút

const ConfirmWantToContinueDialog = ({
  open = true,
  onClose,      // parent: logout + setShowInactivityDialog(false)
  onConfirm,    // parent: setShowInactivityDialog(false) + resetInactivityTimer
  loading = false,
}) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(FIVE_MIN);

  const timerRef = useRef(null);

  // Reset về 5 phút khi mở dialog
  useEffect(() => {
    if (!open) return;
    setCountdown(FIVE_MIN);
  }, [open]);

  // Tick mỗi 1s khi đang mở
  useEffect(() => {
    if (!open) return;
    if (countdown <= 0) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    timerRef.current = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open, countdown]);

  // Hết giờ -> gọi onClose (parent sẽ logout)
  useEffect(() => {
    if (!open) return;
    if (countdown === 0) {
      onClose?.();
    }
  }, [open, countdown, onClose]);

  // Clear timer khi unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleContinue = () => {
    onConfirm?.();
  };

  const handleLogoutClick = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onClose?.();
  };

  return (
    <CustomDialog
      maxWidth="sm"
      open={open}
      onClose={handleLogoutClick}  // backdrop/ESC -> coi như logout do không tương tác
      keepMounted
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold">
              {t('common.want-to-continue')}
            </p>
          </div>
          {/* ❗ Sửa onClick: KHÔNG được gọi onClose() trong render */}
          <GrayIconButton onClick={handleContinue} aria-label="close">
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <p className="pt-5 text-left text-lg text-[#1F2937] font-bold">{t('common.session-will-expire-in')}</p>
        <p className="pt-4 pb-4 text-left text-[32px] text-[#0057FF] font-bold">
          {formatTime(countdown)}
        </p>
        <Trans className='text-[#6B7280]'
          i18nKey="common.confirm-want-to-continue"
          // components={{ b1: <strong />, b2: <strong /> }}
        />
      </DialogContent>

      <DialogActions>
        <div className="flex gap-2 px-4 py-4 w-full">
          <ControlpanelSecondaryButton
            onClick={handleLogoutClick}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 !leading-none"
          >
            <span className="font-medium">{t('button.sign-out')}</span>
          </ControlpanelSecondaryButton>

          <ControlpanelPrimaryButton
            onClick={handleContinue}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 !leading-none"
          >
            <span className="font-medium">{t('button.continue')}</span>
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default ConfirmWantToContinueDialog;
